import { ActionReducerMapBuilder, PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  type ItemSummaryType,
  type RouteSummaryType,
  type ListDeliveriesType,
  DeliveriesType,
} from 'features/Order/pages/PoolingMap/types'
import {
  SLICE_NAME,
  fetchFilters,
  fetchGetListLocationPooling,
  fetchOrderList,
  getDataDeliveryPool,
  tagPoolDataV2,
  untagPoolDataV2,
} from './thunk'

export type OrderPoolingMapSliceType = {
  isLoading: boolean
  isLoadingOverlay: boolean
  isLoadingTag: boolean
  isViewingOrderList: boolean
  popupState: 'CLOSED' | 'FILTER_POPUP'
  listLocation: {
    location_id: number
    location_name: string
    location_type: string
  }[]
  locationSelected: Nullable<{
    location_id: number
    location_name: string
    location_type: string
    location_latitude: string
    location_longitude: string
  }>
  itemSummary: ItemSummaryType
  routeSummary: RouteSummaryType
  clusters: ListDeliveriesType
  selectedDeliverySummary: ListDeliveriesType['delivery_pools'][number]
  originalDeliveries: DeliveriesType
  selectionRevision: number
  removedDelivery: DeliveriesType
  filters: {
    by_status: string[]
    by_sla: string[]
  }
  isochrone: {
    enable_isochrone: boolean
    hub_ids: OrderPoolingMapSliceType['listLocation']
    sla_ids: {
      sla_id: number
      sla_name: string
    }[]
  }
  allClusters: ListDeliveriesType
}

export const initialState: OrderPoolingMapSliceType = {
  isLoading: false,
  isLoadingOverlay: false,
  isLoadingTag: false,
  isViewingOrderList: false,
  popupState: 'CLOSED',
  listLocation: [],
  locationSelected: null,
  itemSummary: {
    total_qty: 0,
    total_sku: 0,
    total_weight: 0,
    total_volume: 0,
    total_distance: 0,
  },
  routeSummary: {
    shapes: [],
    total_distance: 0,
  },
  clusters: {
    location_id: 0,
    delivery_pools: [],
  },
  selectedDeliverySummary: {
    pool_id: 0,
    pool_number: '0',
    summary: {
      items_summary: {
        total_qty: 0,
        total_sku: 0,
        total_weight: 0,
        total_volume: 0,
        total_distance: 0,
      },
      route_summary: {
        shapes: [],
        total_distance: 0,
      },
    },
    deliveries: [],
  },
  removedDelivery: [],
  originalDeliveries: [],
  selectionRevision: 0,
  filters: {
    by_sla: [],
    by_status: [],
  },
  isochrone: {
    enable_isochrone: false,
    sla_ids: [
      {
        sla_id: 4,
        sla_name: '15 Menit',
      },
      {
        sla_id: 5,
        sla_name: '30 Menit',
      },
    ],
    hub_ids: [],
  },
  allClusters: {
    location_id: 0,
    delivery_pools: [],
  },
}

const resetPoolingState = (state: OrderPoolingMapSliceType) => {
  state.isLoadingTag = false
  state.isViewingOrderList = false
  state.selectedDeliverySummary = initialState.selectedDeliverySummary
  state.clusters = {
    ...initialState.clusters,
    location_id: state.clusters.location_id,
  }
  state.removedDelivery = []
  state.originalDeliveries = []
  state.selectionRevision += 1
  state.itemSummary = initialState.itemSummary
  state.routeSummary = initialState.routeSummary
}

const orderPoolingMapSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setIsViewingOrderList: (
      state,
      action: PayloadAction<OrderPoolingMapSliceType['isViewingOrderList']>,
    ) => {
      state.isViewingOrderList = action.payload
    },
    setLocationSelected: (
      state,
      action: PayloadAction<OrderPoolingMapSliceType['locationSelected']>,
    ) => {
      state.locationSelected = action.payload
    },
    setClusters: (state, action: PayloadAction<OrderPoolingMapSliceType['clusters']>) => {
      state.clusters = action.payload
      state.allClusters = action.payload
    },
    setSummary: (
      state,
      action: PayloadAction<{
        items_summary: OrderPoolingMapSliceType['itemSummary']
        route_summary: OrderPoolingMapSliceType['routeSummary']
      }>,
    ) => {
      state.itemSummary = action.payload.items_summary
      state.routeSummary = action.payload.route_summary
    },
    setDeliveries: (
      state,
      action: PayloadAction<OrderPoolingMapSliceType['selectedDeliverySummary']>,
    ) => {
      state.selectedDeliverySummary = action.payload
    },
    setSelectedDeliveries: (
      state,
      action: PayloadAction<OrderPoolingMapSliceType['selectedDeliverySummary']>,
    ) => {
      state.selectedDeliverySummary = action.payload
      state.originalDeliveries = action.payload.deliveries
      state.selectionRevision += 1
      const selectedIds = new Set(action.payload.deliveries.map((delivery) => delivery.order_id))
      state.removedDelivery = state.removedDelivery.filter(
        (delivery) => !selectedIds.has(delivery.order_id),
      )
    },
    restoreSelectedDeliveries: (state) => {
      state.selectedDeliverySummary.deliveries = state.originalDeliveries
      state.removedDelivery = []
      state.selectionRevision += 1
    },
    setRemoveDelivery: (
      state,
      action: PayloadAction<{ order_id: number; pool_number: string }>,
    ) => {
      const { order_id } = action.payload
      const removedDelivery = state.selectedDeliverySummary.deliveries.find(
        (delivery) => delivery.order_id === order_id,
      )
      if (
        removedDelivery?.pool_number &&
        removedDelivery.pool_number !== '0' &&
        !state.removedDelivery.some((delivery) => delivery.order_id === order_id)
      ) {
        state.removedDelivery.push(removedDelivery)
      }

      state.selectedDeliverySummary.deliveries = state.selectedDeliverySummary.deliveries.filter(
        (delivery) => delivery.order_id !== order_id,
      )
    },
    setResetOrderList: (state) => {
      state.selectedDeliverySummary = initialState.selectedDeliverySummary
      state.removedDelivery = initialState.removedDelivery
      state.originalDeliveries = []
      state.selectionRevision += 1
      state.isViewingOrderList = false
    },
    setIsLoadingTag: (state, action: PayloadAction<OrderPoolingMapSliceType['isLoadingTag']>) => {
      state.isLoadingTag = action.payload
    },
    setPopupState: (state, action: PayloadAction<OrderPoolingMapSliceType['popupState']>) => {
      state.popupState = action.payload
    },
    setFilteredClusters: (state, action: PayloadAction<{ [key: string]: string[] }>) => {
      const { by_status, by_sla } = action.payload
      const allClustersCopy: OrderPoolingMapSliceType['allClusters'] = JSON.parse(
        JSON.stringify(state.allClusters),
      )
      const singleOrders = allClustersCopy.delivery_pools.find((pool) => pool.pool_number === '0')
      if (singleOrders) {
        singleOrders.deliveries = singleOrders?.deliveries.filter((order) => {
          const matchesStatus =
            by_status.length === 0 || by_status.includes(order.status?.toLowerCase() || '')
          const matchSLA =
            by_sla.length === 0 ||
            by_sla.includes(
              (
                order.delivery_option.base_timing_desc?.trim() || order.delivery_option.timing_desc
              ).toLowerCase(),
            )
          return matchesStatus && matchSLA
        })
      }

      const orderIdFilteredWithPool = allClustersCopy.delivery_pools.filter((pool) => {
        if (pool.pool_number === '0') return false
        const matchesStatus = pool.deliveries.some(
          (delivery) =>
            by_status.length === 0 || by_status.includes(delivery.status?.toLowerCase() || ''),
        )
        const matchesSLA = pool.deliveries.some(
          (delivery) =>
            by_sla.length === 0 ||
            by_sla.includes(
              (
                delivery.delivery_option.base_timing_desc?.trim() ||
                delivery.delivery_option.timing_desc
              ).toLowerCase(),
            ),
        )
        return matchesStatus && matchesSLA
      })
      state.clusters.delivery_pools = singleOrders
        ? [...orderIdFilteredWithPool, singleOrders]
        : { ...orderIdFilteredWithPool }
    },
    resetFilterCluster: (state) => {
      state.clusters = state.allClusters
    },
    setIsochrone: (state, action) => {
      state.isochrone = action.payload
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<OrderPoolingMapSliceType>) => {
    builder
      .addCase(fetchGetListLocationPooling.fulfilled, (state, action) => {
        state.listLocation = action.payload.data
        state.isochrone.hub_ids = action.payload.data
      })
      .addCase(getDataDeliveryPool.fulfilled, (state, action) => {
        const { items_summary, route_summary } = action.payload.data.summary
        state.itemSummary = items_summary
        state.routeSummary = route_summary
      })
      .addCase(fetchOrderList.pending, (state, action) => {
        if (action.meta.arg.withLoading) {
          state.isLoadingOverlay = true
        }
      })
      .addCase(fetchOrderList.rejected, (state) => {
        state.isLoadingOverlay = false
      })
      .addCase(fetchOrderList.fulfilled, (state, action) => {
        state.isLoadingOverlay = false
        // Assign unique colors to each pool based on pool_id
        const colorMap = new Map()
        const colors = [
          '#8B0000', // Dark Red
          '#006400', // Dark Green
          '#00008B', // Dark Blue
          '#8B008B', // Dark Magenta
          '#008B8B', // Dark Cyan
          '#8B8B00', // Dark Yellow
          '#8B4500', // Dark Orange
          '#4B0082',
        ]

        const colorAndEmptyLabelParser = (data: ListDeliveriesType) => ({
          ...data,
          delivery_pools: data.delivery_pools.map(
            (pool: {
              pool_id: number
              pool_number: string
              summary: {
                items_summary: ItemSummaryType
                route_summary: RouteSummaryType
              }
              deliveries: DeliveriesType
              color?: string
            }) => ({
              ...pool,
              deliveries: pool.deliveries.map((delivery) => ({
                ...delivery,
                delivery_option: {
                  ...delivery.delivery_option,
                  timing_desc:
                    delivery.delivery_option.timing_desc === ''
                      ? 'Jadwal Pengiriman'
                      : delivery.delivery_option.timing_desc,
                },
              })),
              color: colorMap.get(pool.pool_number),
            }),
          ),
        })

        action.payload.data.delivery_pools.forEach(
          (pool: { pool_number: string }, index: number) => {
            if (!colorMap.has(pool.pool_number)) {
              if (pool.pool_number === '0') {
                colorMap.set(pool.pool_number, '#808080') // Grey color for pool_number '0'
              } else {
                colorMap.set(pool.pool_number, colors[index % colors.length])
              }
            }
          },
        )

        // Add color property and scheduled instant to each delivery pool
        state.clusters = colorAndEmptyLabelParser(action.payload.data)
        state.allClusters = colorAndEmptyLabelParser(action.payload.data)
      })
      .addCase(tagPoolDataV2.pending, (state) => {
        state.isLoadingTag = true
      })
      .addCase(tagPoolDataV2.fulfilled, (state) => {
        resetPoolingState(state)
      })
      .addCase(tagPoolDataV2.rejected, (state) => {
        state.isLoadingTag = false
      })
      .addCase(untagPoolDataV2.pending, (state) => {
        state.isLoadingTag = true
      })
      .addCase(untagPoolDataV2.fulfilled, (state) => {
        resetPoolingState(state)
      })
      .addCase(untagPoolDataV2.rejected, (state) => {
        state.isLoadingTag = false
      })
      .addCase(fetchFilters.fulfilled, (state, action) => {
        state.filters = action.payload.data
      })
  },
})

export const {
  setLocationSelected,
  setClusters,
  setSummary,
  setDeliveries,
  setSelectedDeliveries,
  restoreSelectedDeliveries,
  setIsViewingOrderList,
  setResetOrderList,
  setRemoveDelivery,
  setIsLoadingTag,
  setPopupState,
  setFilteredClusters,
  resetFilterCluster,
  setIsochrone,
} = orderPoolingMapSlice.actions
export default orderPoolingMapSlice.reducer
